/**
 * Landliste for kunderegistrering.
 *
 * PowerOffice tar `CountryCode` som ISO 3166-1 alpha-2 på
 * `MailAddress` og validerer ikke mot en kortere liste, så vi tilbyr
 * hele standarden. En kuratert liste ville bare betydd at en kunde i et
 * land vi ikke hadde tenkt på måtte registreres med feil land.
 *
 * Navnene hentes fra `Intl.DisplayNames` i bokmål i stedet for å
 * vedlikeholdes for hånd — 249 landnavn som ikke driver fra hverandre.
 * Faller tilbake til koden hvis en kjøretid mangler navnet.
 */

export interface CountryOption {
  code: string;
  name: string;
}

export const NORWAY: CountryOption = { code: "NO", name: "Norge" };

/** Tildelte ISO 3166-1 alpha-2-koder. */
const ISO_3166_1_ALPHA_2 = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU",
  "AW", "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL",
  "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY", "BZ", "CA", "CC",
  "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV",
  "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE", "EG",
  "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD",
  "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT",
  "GU", "GW", "GY", "HK", "HM", "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM",
  "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH",
  "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK",
  "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH",
  "MK", "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW",
  "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR",
  "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM", "PN", "PR",
  "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC",
  "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL",
  "TM", "TN", "TO", "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY",
  "UZ", "VA", "VC", "VE", "VG", "VI", "VN", "VU", "WF", "WS", "YE", "YT", "ZA",
  "ZM", "ZW",
] as const;

const displayNames = (() => {
  try {
    return new Intl.DisplayNames(["nb"], { type: "region" });
  } catch {
    return null;
  }
})();

function displayName(code: string): string {
  try {
    return displayNames?.of(code) || code;
  } catch {
    return code;
  }
}

/**
 * Alle land unntatt Norge, sortert på norsk navn. Norge er default og
 * har sin egen innenlandsflyt med Brønnøysund-oppslag, så den hører
 * ikke hjemme i utenlands-nedtrekket.
 */
export const FOREIGN_COUNTRIES: CountryOption[] = ISO_3166_1_ALPHA_2
  .filter((code) => code !== NORWAY.code)
  .map((code) => ({ code, name: displayName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name, "nb"));

/** Normaliser til ISO alpha-2. Ugyldig/tom → NO (innenlands default). */
export function normalizeCountryCode(raw: string | null | undefined): string {
  const code = (raw ?? "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "NO";
}

export function isForeignCountry(code: string | null | undefined): boolean {
  return normalizeCountryCode(code) !== NORWAY.code;
}

export function countryName(code: string | null | undefined): string {
  const normalized = normalizeCountryCode(code);
  if (normalized === NORWAY.code) return NORWAY.name;
  return FOREIGN_COUNTRIES.find((c) => c.code === normalized)?.name ?? normalized;
}
